/**
 * Small fuzzy matcher: every query character must appear in order in the candidate.
 * Score rewards consecutive matches, matches at word starts, and short candidates,
 * and penalises gaps. Returns null when the query does not match at all.
 */
export interface FuzzyMatch {
  readonly score: number;
  /** Candidate indices that matched, for highlighting. */
  readonly indices: readonly number[];
}

const isWordStart = (text: string, i: number): boolean => i === 0 || /[\s\-_./:]/.test(text[i - 1] ?? '');

export const fuzzyMatch = (query: string, candidate: string): FuzzyMatch | null => {
  const q = query.toLowerCase();
  const c = candidate.toLowerCase();
  if (q.length === 0) return { score: 0, indices: [] };
  if (q.length > c.length) return null;
  // Exact / prefix / substring get strong fixed scores so they always outrank scattered matches.
  const sub = c.indexOf(q);
  if (q === c) return { score: 1200, indices: range(0, q.length) };
  if (sub === 0) return { score: 1000 - c.length, indices: range(0, q.length) };
  if (sub > 0)
    return { score: 700 - sub - c.length + (isWordStart(c, sub) ? 80 : 0), indices: range(sub, sub + q.length) };

  const indices: number[] = [];
  let score = 0;
  let qi = 0;
  let last = -2;
  for (let ci = 0; ci < c.length && qi < q.length; ci++) {
    if (c[ci] !== q[qi]) continue;
    indices.push(ci);
    score += 10;
    if (ci === last + 1) score += 15;
    if (isWordStart(c, ci)) score += 20;
    if (last >= 0) score -= Math.min(10, ci - last - 1);
    last = ci;
    qi++;
  }
  if (qi < q.length) return null;
  return { score: score - c.length / 4, indices };
};

const range = (from: number, to: number): number[] => Array.from({ length: to - from }, (_, i) => from + i);

/** Ranks items by the best fuzzy score across several searchable fields. */
export const fuzzyRank = <T>(
  query: string,
  items: readonly T[],
  fields: (item: T) => readonly string[],
  limit = 50,
): { item: T; score: number; match: FuzzyMatch | null }[] => {
  const scored: { item: T; score: number; match: FuzzyMatch | null }[] = [];
  for (const item of items) {
    let best: FuzzyMatch | null = null;
    let bestScore = -Infinity;
    let field = 0;
    for (const text of fields(item)) {
      const m = fuzzyMatch(query, text);
      // Earlier fields (name) are worth more than later ones (description).
      const adjusted = m ? m.score - field * 40 : -Infinity;
      if (m && adjusted > bestScore) {
        best = m;
        bestScore = adjusted;
      }
      field++;
    }
    if (best) scored.push({ item, score: bestScore, match: best });
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, limit);
};
